import { AppConfig } from '@/constants/residents/config';

type TokenizeCardParams = {
  account: string; // PAN without spaces
  cvv: string;
  expiry: string; // MMYY
};

type TokenizeAchParams = {
  routingNumber: string;
  accountNumber: string;
};

type CardSecureResponse = {
  token?: string;
  errorcode?: number | string;
  message?: string;
};

export type CardSecureTokenizeBody = {
  account: string; // PAN, or "routing/account" for ACH
  cvv?: string;
  expiry?: string; // MMYY
};

const getCardSecureUrl = (): string => {
  const cardConnectUrl = AppConfig.CARDCONNECT.URL;
  
  if (!cardConnectUrl) {
    console.error('CARDCONNECT.URL is not configured');
    throw new Error('Payment gateway configuration error. Please contact support.');
  }
  
  try {
    const url = new URL(cardConnectUrl);
    return `https://${url.host}/cardsecure/api/v1/ccn/tokenize`;
  } catch (error) {
    console.error('Invalid CARDCONNECT.URL:', cardConnectUrl, error);
    throw new Error('Payment gateway configuration error. Please contact support.');
  }
};

/**
 * The one way this app talks to CardSecure. CardSecure answers cross-origin
 * requests with `Access-Control-Allow-Origin: *` and allows only the
 * Content-Type header (no allow-credentials), so the request must carry no
 * cookies and no bearer token or browsers block it under CORS — which is why
 * mobile web could not add a card while it was routed through the
 * authenticated RTK base query. The resident's JWT must never reach a third
 * party anyway. Never send this through baseQueryWithReauth.
 */
export const postCardSecureTokenize = (body: CardSecureTokenizeBody, signal?: AbortSignal): Promise<Response> =>
  fetch(getCardSecureUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'omit',
    signal,
  });

export const tokenizeCard = async ({ account, cvv, expiry }: TokenizeCardParams): Promise<string> => {
  const resp: CardSecureResponse = await postCardSecureTokenize({ account, cvv, expiry }).then((r) => r.json());

  const token = resp?.token;
  const errorcode = Number(resp?.errorcode ?? 0);
  if (!token || errorcode !== 0) {
    const message = resp?.message || 'Card tokenization failed';
    throw new Error(message);
  }
  return token;
};

export const tokenizeAch = async ({ routingNumber, accountNumber }: TokenizeAchParams): Promise<string> => {
  // For CardSecure ACH, concatenate routing/account (matches existing code behavior)
  const resp: CardSecureResponse = await postCardSecureTokenize({ account: `${routingNumber}/${accountNumber}` }).then(
    (r) => r.json(),
  );

  const token = resp?.token;
  const errorcode = Number(resp?.errorcode ?? 0);
  if (!token || errorcode !== 0) {
    const message = resp?.message || 'ACH tokenization failed';
    throw new Error(message);
  }
  return token;
};


